import type {
  Account,
  Assumptions,
  EventItem,
  FundingScenario,
  FundingSource,
  LargePurchase,
  LoanKind,
  Scenario,
  Snapshot,
  YearBudget,
} from '../types';
import { type MortgageYear } from './mortgage';
import { computeFunding, type FundingResult } from './funding';
import { applyElectiveCaps, scheduleGrowthFactor } from './contributions';
import { reconcileBudget } from './budget';
import {
  acaApplicableFigurePct,
  computeAnnualTax,
  irmaaAnnualSurcharge,
  requiredMinDistribution,
  type AnnualTaxResult,
  type FilingStatus,
} from '../tax/engine';

// ────────────────────────────────────────────────────────────────────────────
// Projection engine (v2 — event-driven)
//
// Runs a year-by-year simulation in real (today's) dollars. Consumes a
// Scenario (or raw events) — the legacy `purchases` input is still accepted
// and converted to purchase events so existing test fixtures keep working.
//
// Buckets: HYSA, Taxable, Retirement. Home/mortgage tracked separately.
// During working years contributions flow in. During retirement spend is met
// by withdrawing Taxable → HYSA → Retirement. Large outflows and inflows are
// expressed as *events* with per-type handlers below.
// ────────────────────────────────────────────────────────────────────────────

export interface ProjectionYear {
  yearOffset: number;
  calendarYear: number;
  age: number;
  status: 'Working' | 'Retired' | 'Gap';

  hysa: BucketRow;
  retirement: BucketRow;
  taxable: BucketRow;
  /** Roth-sleeve sub-row of `retirement`. The Roth balance lives INSIDE the
   *  retirement bucket (a sleeve), so `roth.end` is included in
   *  `retirement.end` rather than added separately. Tracked separately so
   *  the dashboard can show Roth growth and Roth-aware withdrawals can
   *  bypass ordinary-income tax. */
  roth: BucketRow;

  mortgageBalance: number;
  homeValue: number;

  investable: number;
  netWorth: number;

  swrIncome: number;
  requiredSpend: number;
  surplus: number;

  /** Sum of all event outflows that actually drained a bucket this year. */
  purchaseTotal: number;
  /** Planned purchase-style outflow this year (before pool limits). */
  purchasePlanned: number;
  /** Amount of planned outflows that no bucket could cover. */
  purchaseUnfunded: number;
  /** Amount pulled from retirement bucket (may be tax-penalized IRL). */
  purchaseFromRetirement: number;
  /** Annualized non-work income present in this year (SS, pension, income change). */
  extraIncome: number;
  /** Non-mortgage loans that are active at end-of-year (auto, personal,
   *  HELOC, 401(k)). The primary home mortgage is tracked separately via
   *  `mortgageBalance`. */
  loans: ActiveLoanYear[];
  /** Total annual P&I paid across every non-mortgage loan this year. */
  loanPIYear: number;
  /** Sum of outstanding balances on all non-mortgage loans at year-end. */
  totalLoanBalance: number;
  /** Full federal+state tax breakdown for this year — including SS taxation,
   *  LTCG brackets, FICA, NIIT, IRMAA, marginal rates, and Roth-conversion
   *  headroom. Use this to show tax drag over time and as the basis for
   *  bracket-filling recommendations. */
  tax: AnnualTaxResult & {
    /** Annualized IRMAA surcharge (Medicare Part B + D) if age ≥ 65. */
    irmaaSurcharge: number;
    /** Required minimum distribution at this year's age (0 if not applicable). */
    rmd: number;
  };
  /** Per-year cash-flow reconciliation (slice 3). Surfaces what the user
   *  intended to save vs. what cash actually permitted. The auto-reduction
   *  block records HYSA → Taxable → Retirement trims when intended outflows
   *  exceeded after-tax income; `overstretched` flips when even zeroing
   *  every contribution wouldn't have balanced the year. */
  budget: YearBudget;
  /** Convenience mirror of `budget.overstretched` so callers can branch
   *  without dereferencing the budget block. */
  overstretched: boolean;
  /** Human-readable notes about which events fired this year. */
  eventNotes: string[];
}

export interface ActiveLoanYear {
  id: string;
  label: string;
  loanKind: LoanKind;
  annualRate: number;
  originalPrincipal: number;
  /** Year offset when the loan was taken out. */
  startYearOffset: number;
  /** Year offset when the loan is scheduled to be fully paid off. */
  endYearOffset: number;
  /** Outstanding balance at end of this year (0 = paid off this year). */
  balance: number;
  interestPaidThisYear: number;
  principalPaidThisYear: number;
  monthlyPI: number;
  /** ID of the event that originated this loan (lets the UI link back from
   *  the loan strip to the event editor). */
  sourceEventId?: string;
  /** Display name of the originating event, e.g., "New car". */
  sourceEventName?: string;
}

export interface BucketRow {
  beg: number;
  contrib: number;
  withdraw: number;
  purchase: number;
  end: number;
}

export interface ProjectionInput {
  assumptions: Assumptions;
  accounts: Account[];
  /** Latest balance per account. For accounts never updated, we treat 0. */
  currentBalances: Record<string, number>;
  /** Preferred in v2: drives projection from a Scenario. */
  scenario?: Scenario;
  /** Alternate: raw events (used by tests and compare previews). */
  events?: EventItem[];
  /** Legacy input: LargePurchases auto-convert to purchase events. */
  purchases?: LargePurchase[];
  /** How many years to project, default (targetRetirementAge − currentAge) + 15 */
  years?: number;
}

export interface ProjectionResult {
  years: ProjectionYear[];
  mortgage: MortgageYear[];
  requiredSpend: number;
  requiredNestEgg: number;
}

export function sumBucket(
  accounts: Account[],
  balances: Record<string, number>,
  bucket: Account['bucket'],
): number {
  return accounts
    .filter(a => a.bucket === bucket && !a.archived)
    .reduce((s, a) => s + (balances[a.id] ?? 0), 0);
}

/** Sum of starting balances across retirement-bucket accounts whose
 *  `taxSleeve === 'roth'`. The Roth sleeve lives INSIDE the retirement
 *  bucket — its dollars are a subset of `sumBucket(..., 'retirement')` —
 *  so on initial read the engine carries a parallel running total to
 *  remember which fraction is tax-free at withdrawal time. Most existing
 *  accounts have no `taxSleeve` set; those default to pretax (return 0). */
export function sumRothSleeve(
  accounts: Account[],
  balances: Record<string, number>,
): number {
  return accounts
    .filter(a => a.bucket === 'retirement' && a.taxSleeve === 'roth' && !a.archived)
    .reduce((s, a) => s + (balances[a.id] ?? 0), 0);
}

function sumKind(
  accounts: Account[],
  balances: Record<string, number>,
  kind: Account['kind'],
): number {
  return accounts
    .filter(a => a.kind === kind && !a.archived)
    .reduce((s, a) => s + (balances[a.id] ?? 0), 0);
}

export function deriveRequiredSpend(a: Assumptions): number {
  const annualHouseholdSpend = a.monthlyHouseholdSpend * 12;
  const mortgagePIYearly = a.mortgageMonthlyPI * 12;
  const escrowYearly = a.mortgageMonthlyEscrow * 12;
  const baseSpendExMortgage = annualHouseholdSpend - mortgagePIYearly;
  return baseSpendExMortgage + escrowYearly + a.healthcareAnnual;
}

// ── Compensation + ContributionRule resolution (slice 1c) ────────────────────
// Engine reads the new shape: `compensation` for gross wages, `contributionRules`
// summed per bucket for baseline annual contributions. Falls back to legacy
// fields when the new ones are absent (test fixtures, ancient profiles).
// Slice 2 will replace these with cap-aware, percent-of-pay-aware helpers.

/** Baseline gross annual wage income (base + bonus). RSU vests are not yet
 *  consumed by the engine — slice 2 territory. */
export function baselineGrossSalary(a: Assumptions): number {
  if (a.compensation) {
    return a.compensation.baseAnnual + a.compensation.bonusAnnual;
  }
  return a.grossAnnualSalary;
}

/** Sum of all enabled contribution rules targeting a given bucket. Slice 1
 *  only resolves `fixed_dollar` mode; `percent_of_pay` rules contribute 0
 *  here and will be wired up in slice 2 alongside the IRS cap logic. */
export function baselineBucketContrib(
  a: Assumptions,
  bucket: 'hysa' | 'taxable' | 'retirement',
): number {
  if (a.contributionRules !== undefined) {
    return a.contributionRules
      .filter(r => r.enabled && r.target.kind === 'bucket' && r.target.bucket === bucket)
      .reduce((sum, r) => sum + (r.mode.kind === 'fixed_dollar' ? r.mode.annual : 0), 0);
  }
  // Legacy fallback
  if (bucket === 'retirement') return a.annualContribution401k;
  if (bucket === 'taxable') return a.annualContributionTaxable;
  return a.annualContributionHysa;
}

// ── Event helpers ───────────────────────────────────────────────────────────

function resolveEvents(input: ProjectionInput): EventItem[] {
  let events: EventItem[];
  if (input.events) events = input.events.filter(e => e.enabled !== false);
  else if (input.scenario) events = input.scenario.events.filter(e => e.enabled !== false);
  else if (input.purchases) {
    events = input.purchases
      .filter(p => !p.archived && p.enabled !== false)
      .map(legacyPurchaseToEvent);
  } else return [];
  // Stable sort by yearOffset so durational lookups (`salaryAt`, `contribAt`,
  // `monthlySpendAt`) get a chronological view even if the UI ever lets users
  // reorder events. Equal-year events keep their relative input order.
  return events
    .map((e, i) => ({ e, i }))
    .sort((a, b) => a.e.yearOffset - b.e.yearOffset || a.i - b.i)
    .map(x => x.e);
}

function legacyPurchaseToEvent(p: LargePurchase): EventItem {
  const active =
    p.scenarios?.find(s => s.id === p.activeScenarioId) ?? p.scenarios?.[0];
  return {
    id: p.id,
    kind: 'purchase',
    yearOffset: p.yearOffset,
    name: p.name || 'Purchase',
    amount: p.amount,
    newHomeValue: p.newHomeValue,
    funding: active ?? synthFundingFromLegacy(p),
    enabled: p.enabled !== false,
    createdAt: p.createdAt,
  };
}

function synthFundingFromLegacy(p: LargePurchase): FundingScenario | undefined {
  const src = p.sourceAccountId;
  if (!src) return undefined;
  const type: FundingSource['type'] = src.includes('taxable')
    ? 'taxable_ltcg'
    : src.includes('retirement') || src.includes('401k')
      ? 'retirement_pretax'
      : 'hysa';
  const source: FundingSource =
    type === 'hysa'
      ? { id: 'synth', type: 'hysa', amount: p.amount }
      : type === 'taxable_ltcg'
        ? {
            id: 'synth',
            type: 'taxable_ltcg',
            grossAmount: p.amount,
            gainFraction: 0.5,
          }
        : { id: 'synth', type: 'retirement_pretax', grossAmount: p.amount };
  return { id: 'synth', name: 'Default', sources: [source] };
}

/** Build a synthetic FundingScenario for a HomeBuy event so we can reuse
 *  `computeFunding()` for the tax/mortgage/home-sale mechanics. */
function fundingForHomeBuy(e: Extract<EventItem, { kind: 'home_buy' }>): FundingScenario {
  const sources: FundingSource[] = [];
  if (e.sellCurrentHome) {
    sources.push({
      id: 's-sell',
      type: 'home_sale',
      salePrice: 0, // engine fills in at event time using current homeValue
      useSection121: true,
    });
  }
  if (e.downPaymentFunding) sources.push(...e.downPaymentFunding.sources);
  if (e.mortgagePrincipal > 0) {
    sources.push({
      id: 's-mortgage',
      type: 'mortgage',
      principal: e.mortgagePrincipal,
      annualRate: e.mortgageAnnualRate,
      termYears: e.mortgageTermYears,
    });
  }
  return { id: 'synth-home', name: 'Home buy', sources };
}

function fundingForHomeSell(e: Extract<EventItem, { kind: 'home_sell' }>): FundingScenario {
  return {
    id: 'synth-sell',
    name: 'Home sell',
    sources: [
      {
        id: 's',
        type: 'home_sale',
        salePrice: e.salePrice,
        useSection121: e.useSection121,
        costBasisOverride: e.costBasisOverride,
      },
    ],
  };
}

// ── Durational effect tracking ──────────────────────────────────────────────
//
// Job gaps and fixed-benefit streams (SS/pension) are modeled as effects on
// an active-effects list, processed per year. Absolute-valued events
// (income_change, contribution_change, spending_change) are resolved via
// snapshot queries over the event list — "last change before this year
// wins" — so stacking is intuitive.

interface DurEffect {
  startYear: number;
  endYear: number; // exclusive; Infinity = never ends
}
interface GapEffect extends DurEffect { kind: 'gap'; monthlySpendOverride?: number; }
interface SsEffect extends DurEffect { kind: 'ss'; monthly: number; }
interface PensionEffect extends DurEffect { kind: 'pension'; monthly: number; }
type AnyEffect = GapEffect | SsEffect | PensionEffect;

function eventToEffect(e: EventItem, y: number): AnyEffect | null {
  switch (e.kind) {
    case 'job_gap':
      return {
        kind: 'gap',
        startYear: y,
        endYear: y + e.durationYears,
        monthlySpendOverride: e.monthlySpendOverride,
      };
    case 'social_security':
      return { kind: 'ss', startYear: y, endYear: Infinity, monthly: e.monthlyBenefit };
    case 'pension':
      return {
        kind: 'pension',
        startYear: y,
        endYear: e.durationYears ? y + e.durationYears : Infinity,
        monthly: e.monthlyBenefit,
      };
    default:
      return null;
  }
}

/** Snapshot query: the effective gross salary at year `y`, taking all
 *  income_change events into account. Last one wins. */
function salaryAt(y: number, events: EventItem[], baseline: number): number {
  let current = baseline;
  for (const e of events) {
    if (e.kind !== 'income_change' || e.enabled === false) continue;
    if (e.yearOffset > y) continue;
    const expired = e.durationYears !== undefined && y >= e.yearOffset + e.durationYears;
    if (!expired) current = e.newAnnualSalary;
  }
  return current;
}

/** Snapshot query: effective contribution for a given bucket. */
function contribAt(
  y: number,
  events: EventItem[],
  field: 'newRetirement' | 'newTaxable' | 'newHysa',
  baseline: number,
): number {
  let current = baseline;
  for (const e of events) {
    if (e.kind !== 'contribution_change' || e.enabled === false) continue;
    if (e.yearOffset > y) continue;
    const expired = e.durationYears !== undefined && y >= e.yearOffset + e.durationYears;
    if (expired) continue;
    const v = e[field];
    if (v !== undefined) current = v;
  }
  return current;
}

interface LoanState {
  id: string;
  label: string;
  loanKind: LoanKind;
  originalPrincipal: number;
  annualRate: number;
  termYears: number;
  monthlyPI: number;
  balance: number;
  startYearOffset: number;
  endYearOffset: number;
  sourceEventId?: string;
  sourceEventName?: string;
}

/** Snapshot query: effective monthly household spend. */
function monthlySpendAt(y: number, events: EventItem[], baseline: number): number {
  let current = baseline;
  for (const e of events) {
    if (e.kind !== 'spending_change' || e.enabled === false) continue;
    if (e.yearOffset > y) continue;
    const expired = e.durationYears !== undefined && y >= e.yearOffset + e.durationYears;
    if (!expired) current = e.newMonthlySpend;
  }
  return current;
}

// ── Main projection ─────────────────────────────────────────────────────────

export function project(input: ProjectionInput): ProjectionResult {
  const { assumptions, accounts, currentBalances } = input;
  const events = resolveEvents(input);

  // Apply any retire events at year 0 (they shift retirement forward/back);
  // later years' retire events are applied in-loop.
  let retirementAge = assumptions.targetRetirementAge;
  for (const e of events) {
    if (e.kind === 'retire' && e.yearOffset === 0) retirementAge = e.retirementAge;
  }

  const yearsToRetirement = retirementAge - assumptions.currentAge;
  const totalYears = input.years ?? Math.max(15, yearsToRetirement + 15);

  // Initial bucket balances. The Roth sleeve (`roth`) is a sub-balance of
  // the retirement bucket: it's *included* in `r`, not added on top. Tracking
  // it parallel lets the engine apply tax-free withdrawals and Roth-routed
  // contributions without splitting the projection into N parallel buckets.
  let h = sumBucket(accounts, currentBalances, 'hysa');
  let r = sumBucket(accounts, currentBalances, 'retirement');
  let roth = sumRothSleeve(accounts, currentBalances);
  let t = sumBucket(accounts, currentBalances, 'taxable');
  let homeValue = sumKind(accounts, currentBalances, 'home');
  let mortgageBal = sumKind(accounts, currentBalances, 'mortgage');
  let mortgageRate = assumptions.mortgageAnnualRate;
  let mortgageMonthlyPI = assumptions.mortgageMonthlyPI;
  let homeCostBasis = assumptions.homeCostBasis;

  const baseSpendForNestEgg = deriveRequiredSpend(assumptions);
  const requiredNestEgg = baseSpendForNestEgg / assumptions.swr;

  const mortgageSchedule: MortgageYear[] = [];
  const rows: ProjectionYear[] = [];
  const activeEffects: AnyEffect[] = [];
  // Non-mortgage loans (auto/personal/HELOC/401k). Each amortizes every
  // year; the annual P&I is deducted from cash flow, and the remaining
  // balance counts as a liability in net worth. 401(k) loans additionally
  // re-credit principal payments to the retirement bucket.
  const activeLoans: LoanState[] = [];

  for (let y = 0; y < totalYears; y++) {
    const age = assumptions.currentAge + y;
    const calendarYear = assumptions.startingCalendarYear + y;
    const eventsThisYear = events.filter(e => e.yearOffset === y);
    const eventNotes: string[] = [];

    // ── Retire events (shift the retirement age) ─────────────────────────
    for (const e of eventsThisYear) {
      if (e.kind === 'retire') {
        retirementAge = e.retirementAge;
        eventNotes.push(`Retirement age → ${retirementAge}`);
      }
    }

    // ── Register new durational effects starting this year ───────────────
    for (const e of eventsThisYear) {
      const eff = eventToEffect(e, y);
      if (eff) activeEffects.push(eff);
      if (e.kind === 'job_gap') eventNotes.push(`${e.name} (${e.durationYears}y)`);
      if (e.kind === 'income_change') {
        eventNotes.push(`${e.name}: salary → ${Math.round(e.newAnnualSalary).toLocaleString()}/yr`);
      }
      if (e.kind === 'contribution_change') eventNotes.push(e.name);
      if (e.kind === 'spending_change') {
        eventNotes.push(`${e.name}: spend → ${Math.round(e.newMonthlySpend).toLocaleString()}/mo`);
      }
      if (e.kind === 'social_security') eventNotes.push(`SS starts: ${Math.round(e.monthlyBenefit).toLocaleString()}/mo`);
      if (e.kind === 'pension') eventNotes.push(`Pension starts: ${Math.round(e.monthlyBenefit).toLocaleString()}/mo`);
    }

    // Summarize active effects for this year
    const activeNow = activeEffects.filter(x => x.startYear <= y && y < x.endYear);
    const onGap = activeNow.some(x => x.kind === 'gap');
    const gapOverride = activeNow.find(x => x.kind === 'gap') as GapEffect | undefined;
    // Fixed-benefit streams (SS, pension) come from the effects list.
    const benefitIncomeAnnual = activeNow.reduce((s, x) => {
      if (x.kind === 'ss' || x.kind === 'pension') return s + x.monthly * 12;
      return s;
    }, 0);

    // Snapshot-resolved values from absolute events. Salary and contribution
    // baselines come from `compensation` + `contributionRules` (slice 1c);
    // legacy assumption fields remain as a fallback while migration is in
    // flight and for ancient profiles.
    const baselineSalary = baselineGrossSalary(assumptions);
    const effectiveSalary = salaryAt(y, events, baselineSalary);
    const salaryDelta = effectiveSalary - baselineSalary;
    const effectiveMonthlySpend = monthlySpendAt(y, events, assumptions.monthlyHouseholdSpend);

    // ── Cap-aware contribution resolution (Slice 2) ─────────────────────
    // Resolve every ContributionRule against this year's compensation,
    // applying 402(g), 415(c), and IRA caps in user-defined priority order.
    // Per-sleeve totals drive both bucket routing AND the tax pipeline
    // (only `pretaxTotal` reduces W-2 wages; Roth contributions don't).
    //
    // Legacy fallback: if `contributionRules` is undefined (ancient profile)
    // we fall back to the simple per-bucket sum the slice-1c helpers
    // produce — no caps, no Roth sleeve.
    const compThisYear = assumptions.compensation ?? {
      baseAnnual: baselineSalary,
      bonusAnnual: 0,
      bonusMonth: 2 as const,
    };
    // Scale rule resolution by the effective salary if it differs from baseline
    // (income_change events): for percent_of_pay rules this lets a raise grow
    // the contribution; for fixed_dollar it has no effect.
    const compForYear = {
      ...compThisYear,
      baseAnnual: compThisYear.baseAnnual * (effectiveSalary / Math.max(1, baselineSalary)),
      bonusAnnual: compThisYear.bonusAnnual * (effectiveSalary / Math.max(1, baselineSalary)),
    };
    const capResult = assumptions.contributionRules
      ? applyElectiveCaps(assumptions.contributionRules, compForYear, age)
      : null;

    const working = age < retirementAge && !onGap;
    const status: ProjectionYear['status'] = onGap ? 'Gap' : working ? 'Working' : 'Retired';

    // Cap-aware totals (preferred) OR legacy per-bucket totals (fallback).
    // The cap-aware totals already excluded disabled rules / capped overage;
    // contribution_change events below still override the FINAL bucket
    // totals for backward compatibility (existing tests + any user who wrote
    // a contribution_change event expecting it to set the bucket exactly).
    const capRetirement = capResult
      ? capResult.pretaxTotal + capResult.rothTotal + capResult.afterTax401kTotal
      : 0;
    const capTaxable = capResult ? capResult.taxableTotal : 0;
    const capHysa = capResult ? capResult.hysaTotal : 0;
    const baselineRetirement = capResult
      ? capRetirement
      : baselineBucketContrib(assumptions, 'retirement');
    const baselineTaxable = capResult
      ? capTaxable
      : baselineBucketContrib(assumptions, 'taxable');
    const baselineHysa = capResult ? capHysa : baselineBucketContrib(assumptions, 'hysa');
    const effectiveContribR = contribAt(y, events, 'newRetirement', baselineRetirement);
    const effectiveContribT = contribAt(y, events, 'newTaxable', baselineTaxable);
    const effectiveContribH = contribAt(y, events, 'newHysa', baselineHysa);

    // ── Slice 4: per-rule timing entries for time-weighted bucket growth ──
    //
    // Each rule's dollars are time-weighted by its `ContributionSchedule`:
    //   - lump_sum:1 (Jan)  → full year of growth
    //   - monthly           → ~half a year of growth (closed-form factor)
    //   - lump_sum:12 (Dec) → ~one month of growth
    //   - with_bonus        → growth like a lump_sum at comp.bonusMonth
    //
    // We build a per-bucket array of (post-cap) rule contributions tagged
    // with each rule's growth factor. Later steps (contribution_change
    // scaling, budget-trim auto-reduction) update the dollars in place;
    // factors stay constant. At end-of-year growth, the rule portion of
    // each bucket grows by its weighted sum, and only the non-rule portion
    // (prior balance + non-rule inflows − pulls) takes the full (1+r).
    interface RuleTimingEntry {
      ruleId: string;
      dollars: number;
      factor: number;
      /** Identifies pretax 401(k) entries within the retirement bucket so the
       *  budget reconciler can skip them (pretax stays sacrosanct). */
      isPretaxRetirement: boolean;
    }
    const ruleTimingHysa: RuleTimingEntry[] = [];
    const ruleTimingTaxable: RuleTimingEntry[] = [];
    const ruleTimingRetirement: RuleTimingEntry[] = [];
    if (capResult && working && assumptions.contributionRules) {
      for (const r of assumptions.contributionRules) {
        const dollars = capResult.byRule.get(r.id) ?? 0;
        if (dollars <= 0) continue;
        const tt = r.taxTreatment;
        // Bucket routing mirrors caps.ts's tally logic.
        let bucket: 'hysa' | 'taxable' | 'retirement';
        if (
          tt === 'pretax' ||
          tt === 'roth_401k' ||
          tt === 'after_tax_401k' ||
          tt === 'mega_backdoor_to_roth' ||
          tt === 'roth_ira'
        ) {
          bucket = 'retirement';
        } else if (tt === 'after_tax_brokerage') {
          bucket = 'taxable';
        } else if (r.target.kind === 'bucket') {
          bucket = r.target.bucket;
        } else {
          // account-targeted with no taxTreatment — fall back to retirement
          // (the safest default; this MVP doesn't otherwise model account
          // targets, so it ends up unused).
          bucket = 'retirement';
        }
        const realReturn =
          bucket === 'hysa' ? assumptions.hysaRealReturn : assumptions.stockRealReturn;
        const factor = scheduleGrowthFactor(
          r.schedule,
          realReturn,
          compForYear.bonusMonth,
        );
        const entry: RuleTimingEntry = {
          ruleId: r.id,
          dollars,
          factor,
          isPretaxRetirement: bucket === 'retirement' && tt === 'pretax',
        };
        if (bucket === 'hysa') ruleTimingHysa.push(entry);
        else if (bucket === 'taxable') ruleTimingTaxable.push(entry);
        else ruleTimingRetirement.push(entry);
      }
    }

    // contribution_change events scale the bucket TOTAL — preserve the
    // schedule mix by scaling each entry by the same ratio. This way a
    // bucket with mixed monthly + lump rules stays mixed proportionally
    // even when the user authored "set retirement to $X".
    const scaleEntries = (entries: RuleTimingEntry[], baseline: number, effective: number) => {
      if (baseline <= 0 || baseline === effective) return;
      const ratio = effective / baseline;
      for (const e of entries) e.dollars = Math.max(0, e.dollars * ratio);
    };
    scaleEntries(ruleTimingHysa, baselineHysa, effectiveContribH);
    scaleEntries(ruleTimingTaxable, baselineTaxable, effectiveContribT);
    scaleEntries(ruleTimingRetirement, baselineRetirement, effectiveContribR);

    // Base contributions — absolute, not clamped (a negative contribution
    // represents "the shortfall comes out of that bucket", handled naturally
    // when we fold into pools below).
    let hysaContrib = working ? effectiveContribH : 0;
    let retirementContrib = working ? effectiveContribR : 0;
    let taxableContrib = working ? effectiveContribT : 0;

    // ── Roth-sleeve sub-contribution + employer match (Slice 2) ─────────
    // The retirement-bucket dollars include any Roth-sleeve dollars; we
    // track the sleeve total separately so future withdrawals can be
    // routed last (tax-free). Employer match is added on TOP of retirement
    // contributions (it doesn't count against 402(g) and isn't part of any
    // user rule). It lands in pretax sleeve — standard match treatment.
    let rothContribThisYear = 0;
    let employerMatchThisYear = 0;
    if (capResult && working) {
      // Scale sleeve totals by the same effectiveContribR/baselineRetirement
      // ratio so contribution_change overrides scale Roth proportionally.
      // (If a user "set retirement to $X" via contribution_change and rules
      // were the source, we preserve the sleeve mix.)
      const ratio =
        baselineRetirement > 0 ? effectiveContribR / baselineRetirement : 0;
      rothContribThisYear = Math.max(0, capResult.rothTotal * ratio);
      employerMatchThisYear = capResult.employerMatch * ratio;
      retirementContrib += employerMatchThisYear;
      // Surface cap warnings.
      for (const [ruleId, dollars] of capResult.cappedByRule) {
        if (dollars <= 0) continue;
        const ruleName =
          assumptions.contributionRules?.find(r => r.id === ruleId)?.name ?? ruleId;
        const reason = capResult.capReasonByRule.get(ruleId);
        const reasonLabel =
          reason === '402g'
            ? 'IRS 402(g) limit'
            : reason === '415c'
              ? 'IRS 415(c) limit'
              : reason === 'ira'
                ? 'IRS IRA limit'
                : 'IRS limit';
        eventNotes.push(
          `⚠ ${ruleName} capped at ${reasonLabel}: ${Math.round(dollars).toLocaleString()} did not land`,
        );
      }
      // After-tax 401(k) without mega-backdoor still goes to retirement
      // bucket but lands in PRETAX sleeve in this MVP. Surface a hint so
      // users know to switch the treatment if they want Roth handling.
      if (capResult.afterTax401kTotal > 0) {
        eventNotes.push(
          `after_tax_401k: ${Math.round(capResult.afterTax401kTotal).toLocaleString()} routed to pretax sleeve. ` +
            `Use mega_backdoor_to_roth treatment for proper Roth-sleeve handling.`,
        );
      }
    }

    // Salary delta posts to taxable after a rough post-tax haircut. Positive
    // deltas add to savings; negative deltas (pay cuts / new lower-paying
    // jobs) pull savings out of taxable. Benefit income (SS/pension) during
    // working years also flows to taxable; during retirement it offsets
    // withdrawals (handled later).
    const extraIncomeAnnual = salaryDelta + (working ? benefitIncomeAnnual : 0);
    if (working && extraIncomeAnnual !== 0) {
      const postTax =
        extraIncomeAnnual * (1 - assumptions.ordinaryRate - assumptions.stateTaxRate);
      taxableContrib += postTax;
    }

    // ── Collect funding requests from all outflow events this year ───────
    let reqHysa = 0;
    let reqTaxable = 0;
    let reqRetirement = 0;
    let purchasePlanned = 0;
    let purchaseUnfunded = 0;
    let cashFlowThisYear = 0;
    let homeReplacedTo: number | null = null;

    const applyFundingResult = (
      name: string,
      planned: number,
      funding: FundingResult,
      sourceEventId?: string,
    ) => {
      purchasePlanned += planned;
      reqHysa += funding.pullHysa;
      reqTaxable += funding.pullTaxable;
      reqRetirement += funding.pullRetirement;
      purchaseUnfunded += funding.unfunded;
      cashFlowThisYear += funding.cashFlow;
      if (funding.homeSold) {
        homeValue = 0;
        mortgageBal = 0;
        mortgageMonthlyPI = 0;
        mortgageRate = 0;
      }
      if (funding.newMortgagePrincipal > 0) {
        mortgageBal += funding.newMortgagePrincipal;
        mortgageMonthlyPI = funding.newMortgageMonthlyPI;
        mortgageRate = funding.newMortgageAnnualRate;
      }
      for (const ln of funding.newLoans) {
        activeLoans.push({
          id: ln.id,
          label: ln.label,
          loanKind: ln.loanKind,
          originalPrincipal: ln.principal,
          annualRate: ln.annualRate,
          termYears: ln.termYears,
          monthlyPI: ln.monthlyPI,
          balance: ln.principal,
          startYearOffset: y,
          endYearOffset: y + ln.termYears,
          sourceEventId,
          sourceEventName: name,
        });
        eventNotes.push(
          `${ln.label}: +${Math.round(ln.principal).toLocaleString()} @ ${(ln.annualRate * 100).toFixed(2)}% / ${ln.termYears}y`,
        );
      }
      if (funding.unfunded > 0) {
        eventNotes.push(`⚠ ${name}: ${Math.round(funding.unfunded).toLocaleString()} unfunded`);
      }
    };

    // Purchase events
    for (const e of eventsThisYear) {
      if (e.kind !== 'purchase') continue;
      const funding = e.funding ?? { id: 'empty', name: 'Empty', sources: [] };
      const result = computeFunding(funding, assumptions, {
        currentMortgageBalance: mortgageBal,
        ageAtPurchase: age,
        purchaseAmount: e.amount,
        working,
      });
      applyFundingResult(e.name, e.amount, result, e.id);
      if (typeof e.newHomeValue === 'number' && e.newHomeValue > 0) {
        homeReplacedTo = e.newHomeValue;
      }
    }

    // Gift events (outflow, no asset replacement)
    for (const e of eventsThisYear) {
      if (e.kind !== 'gift') continue;
      const funding = e.funding ?? { id: 'empty', name: 'Empty', sources: [] };
      const result = computeFunding(funding, assumptions, {
        currentMortgageBalance: mortgageBal,
        ageAtPurchase: age,
        purchaseAmount: e.amount,
        working,
      });
      applyFundingResult(e.name, e.amount, result, e.id);
    }

    // Home sell events run BEFORE home buys in the same year. If a user
    // authored both (sell old + buy new) without using home_buy's built-in
    // sellCurrentHome flag, we want the old mortgage paid off from sale
    // proceeds first; otherwise home_buy stacks its new mortgage on top of
    // the old one and the home_sell can't cover it.
    for (const e of eventsThisYear) {
      if (e.kind !== 'home_sell') continue;
      const funding = fundingForHomeSell(e);
      const result = computeFunding(funding, assumptions, {
        currentMortgageBalance: mortgageBal,
        ageAtPurchase: age,
        purchaseAmount: 0,
        working,
      });
      // Net proceeds go into the specified bucket
      const toHysa = e.proceedsBucket === 'hysa' ? result.homeSaleNet : 0;
      const toTaxable = e.proceedsBucket === 'taxable' ? result.homeSaleNet : 0;
      // Apply home side-effects
      homeValue = 0;
      mortgageBal = 0;
      mortgageMonthlyPI = 0;
      mortgageRate = 0;
      // Add to contributions this year so the existing pool logic picks it up
      hysaContrib += toHysa;
      taxableContrib += toTaxable;
      eventNotes.push(`Sold home: +${Math.round(result.homeSaleNet).toLocaleString()} to ${e.proceedsBucket}`);
    }

    // Home buy events: sell current home (if flagged) + install new mortgage + pull down payment
    for (const e of eventsThisYear) {
      if (e.kind !== 'home_buy') continue;
      // Build a funding scenario on the fly. If sellCurrentHome, patch the
      // home_sale source's salePrice to the current homeValue.
      const funding = fundingForHomeBuy(e);
      const patched: FundingScenario = {
        ...funding,
        sources: funding.sources.map(s =>
          s.type === 'home_sale'
            ? { ...s, salePrice: homeValue, costBasisOverride: homeCostBasis }
            : s,
        ),
      };
      const result = computeFunding(patched, assumptions, {
        currentMortgageBalance: mortgageBal,
        ageAtPurchase: age,
        purchaseAmount: e.downPayment,
        working,
      });
      applyFundingResult(e.name, e.downPayment, result, e.id);
      homeValue = e.purchasePrice;
      homeReplacedTo = null; // we've already set it
      homeCostBasis = e.newCostBasis;
      eventNotes.push(`Bought ${e.name}: ${e.purchasePrice.toLocaleString()}`);
    }

    // Home improvement events: out-of-pocket cost + home value bump.
    // The engine treats `cost` as the purchase outflow (funded however the
    // user specifies), bumps `homeValue` by `valueAdded`, and optionally
    // adds the cost to `homeCostBasis` for future §121 calculations.
    for (const e of eventsThisYear) {
      if (e.kind !== 'home_improvement') continue;
      const funding = e.funding ?? { id: 'empty', name: 'Empty', sources: [] };
      const result = computeFunding(funding, assumptions, {
        currentMortgageBalance: mortgageBal,
        ageAtPurchase: age,
        purchaseAmount: e.cost,
        working,
      });
      applyFundingResult(e.name, e.cost, result, e.id);
      if (e.valueAdded > 0) homeValue += e.valueAdded;
      if (e.addToCostBasis) homeCostBasis += e.cost;
      eventNotes.push(
        `${e.name}: ${Math.round(e.cost).toLocaleString()} spent · +${Math.round(e.valueAdded).toLocaleString()} home value${e.addToCostBasis ? ' (basis +' + Math.round(e.cost).toLocaleString() + ')' : ''}`,
      );
    }

    // Refinance events: replace mortgage terms from this year forward
    for (const e of eventsThisYear) {
      if (e.kind !== 'refinance') continue;
      if (mortgageBal <= 0) {
        eventNotes.push(`Refi skipped (no active mortgage)`);
        continue;
      }
      mortgageRate = e.newAnnualRate;
      const m = e.newAnnualRate / 12;
      const n = e.newTermYears * 12;
      const bal = mortgageBal + (e.cashOut ?? 0);
      mortgageBal = bal;
      mortgageMonthlyPI =
        m === 0 ? bal / n : (bal * m * Math.pow(1 + m, n)) / (Math.pow(1 + m, n) - 1);
      if (e.cashOut && e.cashOut > 0) {
        hysaContrib += e.cashOut;
      }
      eventNotes.push(`Refinanced at ${(e.newAnnualRate * 100).toFixed(2)}%, ${e.newTermYears}y`);
    }

    // Windfall events: direct deposit
    for (const e of eventsThisYear) {
      if (e.kind !== 'windfall') continue;
      const net = e.taxFree
        ? e.amount
        : e.amount * (1 - (assumptions.ordinaryRate + assumptions.stateTaxRate));
      if (e.toBucket === 'hysa') hysaContrib += net;
      else if (e.toBucket === 'taxable') taxableContrib += net;
      else retirementContrib += net;
      eventNotes.push(
        `${e.name}: +${Math.round(net).toLocaleString()} to ${e.toBucket}${e.taxFree ? ' (tax-free)' : ''}`,
      );
    }

    // Roth conversions: pull principal from pretax sleeve, deposit to Roth
    // sleeve, and pay tax (computed via the marginal-cost delta below in the
    // tax pipeline — NOT the flat-rate haircut the legacy code used). The
    // tax dollars are pulled from HYSA via reqHysa; the actual amount is
    // determined later in the year by computing tax twice (with vs. without
    // the conversion) and using the difference. For now, accumulate the
    // gross conversion amount so the tax pass can reference it.
    let rothConversionAmount = 0;
    for (const e of eventsThisYear) {
      if (e.kind !== 'roth_conversion') continue;
      rothConversionAmount += e.amount;
      eventNotes.push(`Roth conversion: ${Math.round(e.amount).toLocaleString()} (tax computed at marginal bracket)`);
    }

    // (cash_flow event funding is reconciled later in the unified budget pass.)

    // Apply home replacement (non-buy purchase with newHomeValue)
    if (homeReplacedTo !== null) homeValue = homeReplacedTo;

    // ── Amortize active non-mortgage loans ───────────────────────────────
    // Each loan's annual P&I comes out of cash flow: during working years
    // we reduce HYSA contributions first (then taxable, then retirement —
    // an "absorb-shortfall" spillover); during retirement/gap years the
    // payments add to required spend so the existing withdrawal logic
    // handles them. For 401(k) loans we re-credit the principal portion
    // to the retirement bucket (money repaid to self).
    const loanActivityThisYear: ActiveLoanYear[] = [];
    let totalLoanPIThisYear = 0;
    let total401kPrincipalRepaidThisYear = 0;
    let total401kInterestRepaidThisYear = 0;
    for (const loan of activeLoans) {
      if (loan.balance <= 0) continue;
      const beg = loan.balance;
      const m = loan.annualRate / 12;
      const end =
        m === 0
          ? Math.max(0, beg - loan.monthlyPI * 12)
          : Math.max(
              0,
              beg * Math.pow(1 + m, 12) -
                (loan.monthlyPI * (Math.pow(1 + m, 12) - 1)) / m,
            );
      const principalPaid = beg - end;
      const annualPI = Math.min(loan.monthlyPI * 12, beg + beg * loan.annualRate);
      const interestPaid = beg > 0 ? Math.max(0, annualPI - principalPaid) : 0;
      loan.balance = end;
      totalLoanPIThisYear += annualPI;
      if (loan.loanKind === 'retirement_401k') {
        total401kPrincipalRepaidThisYear += principalPaid;
        total401kInterestRepaidThisYear += interestPaid;
      }
      loanActivityThisYear.push({
        id: loan.id,
        label: loan.label,
        loanKind: loan.loanKind,
        annualRate: loan.annualRate,
        originalPrincipal: loan.originalPrincipal,
        startYearOffset: loan.startYearOffset,
        endYearOffset: loan.endYearOffset,
        balance: end,
        interestPaidThisYear: interestPaid,
        principalPaidThisYear: principalPaid,
        monthlyPI: loan.monthlyPI,
        sourceEventId: loan.sourceEventId,
        sourceEventName: loan.sourceEventName,
      });
    }
    // 401(k) loans repay themselves — both principal and interest go back
    // into the retirement bucket. (When you take a 401k loan, you're
    // borrowing from your own account; repayments — including the interest
    // portion — are credited back to your retirement balance.)
    const total401kRepaidThisYear =
      total401kPrincipalRepaidThisYear + total401kInterestRepaidThisYear;
    if (total401kRepaidThisYear > 0) {
      retirementContrib += total401kRepaidThisYear;
    }
    // (working-year loan P&I is folded into `mandatoryOut` in the unified
    // budget pass below; retired/gap loan P&I is added to `spendThisYear`.)

    // ── Slice 3: unified budget reconciliation ───────────────────────────
    //
    // Replaces the old "cash_flow → contribution-trim" loop AND the old
    // "loan P&I → contribution-trim" loop with a single per-year pass that
    // reconciles intended contributions against after-tax cash inflows.
    //
    // Convention (from src/engine/budget.ts and the YearBudget interface):
    //   - Pretax 401(k) deferrals are INVISIBLE to the budget. They never
    //     appeared as take-home pay (W-2 wage reduction) and shouldn't be
    //     reduced if cash is short — that would change the user's tax
    //     picture mid-year, which we don't support. So we split the
    //     retirement bucket into pretax (untouchable) and after-tax
    //     (Roth, mega-backdoor, after-tax-401k, employer match, 401k loan
    //     repayments — everything that doesn't reduce W-2 wages).
    //   - Pass-through items (post-tax salary delta, post-tax benefits,
    //     net windfalls) are ALREADY included in `taxableContrib` /
    //     `retirementContrib` / `hysaContrib` by the code above. They are
    //     also included in `cashIn` here so the two sides net out
    //     symmetrically (cash in = bucket in for these items).
    //
    // Only working-year reconciliation matters in practice. For retired/
    // gap years, contributions are zero (set above by `working ? … : 0`),
    // and spend is met from withdrawals — the budget block still gets
    // stamped for completeness but auto-reduction is always zero.
    const pretaxRetirementContribForBudget = working
      ? capResult
        ? capResult.pretaxTotal *
          (baselineRetirement > 0 ? effectiveContribR / baselineRetirement : 1)
        : effectiveContribR
      : 0;
    const intendedHysa = Math.max(0, hysaContrib);
    const intendedTaxable = Math.max(0, taxableContrib);
    // After-tax retirement = total retirement contribs minus pretax sleeve
    // minus 401(k)-loan repayments (which are mandatory cash flow, not
    // user-discretionary savings — already counted in `mandatoryOut` via
    // `totalLoanPIThisYear` and netted to retirement as a wash).
    const intendedAfterTaxRetirement = Math.max(
      0,
      retirementContrib -
        pretaxRetirementContribForBudget -
        total401kRepaidThisYear,
    );
    // Cash inflows the user actually sees as take-home. The real
    // computeAnnualTax runs LATER (after withdrawals + Roth conversions),
    // but we need a working-year estimate now so the budget can trim
    // contributions if cash is short. We run a wage-only tax preview here
    // (no withdrawals, no SS, no LTCG) — cheap, and produces a tight
    // estimate for working years where wages dominate.
    const wageBaseAfterPretax = working
      ? Math.max(0, effectiveSalary - pretaxRetirementContribForBudget)
      : 0;
    let wagesPostTax = 0;
    if (working && wageBaseAfterPretax > 0) {
      const wagePreview = computeAnnualTax({
        status: assumptions.filingStatus as FilingStatus,
        wages: wageBaseAfterPretax,
        otherOrdinary: 0,
        ltcg: 0,
        ssBenefits: 0,
        netInvestmentIncome: 0,
        stateRate: assumptions.stateTaxRate,
      });
      wagesPostTax = Math.max(0, wageBaseAfterPretax - wagePreview.totalTax);
    }
    // Pass-through cash from salary-delta + working-year benefits + net
    // windfalls. These were ADDED into taxableContrib above; mirror them
    // into cashIn so the budget sees the symmetric pair (in == bucket).
    // The flat-rate haircut matches what the salary-delta `postTax`
    // computation above already used (so the two sides stay symmetric).
    const flatTaxRate = assumptions.ordinaryRate + assumptions.stateTaxRate;
    const extraIncomePostTax = working
      ? Math.max(0, extraIncomeAnnual) * (1 - flatTaxRate)
      : 0;
    const windfallToBucketsThisYear = working
      ? eventsThisYear.reduce((s, e) => {
          if (e.kind !== 'windfall') return s;
          return (
            s +
            (e.taxFree ? e.amount : e.amount * (1 - flatTaxRate))
          );
        }, 0)
      : 0;
    const cashInForBudget = wagesPostTax + extraIncomePostTax + windfallToBucketsThisYear;

    // Mandatory outflows: spend (ex mortgage P&I, since we add active
    // mortgage P&I separately) + escrow + healthcare + active mortgage P&I
    // + non-mortgage loan P&I. ACA / IRMAA are added later by the tax
    // pass; for the budget we use the unsubsidized `healthcareAnnual` —
    // which is also what shows up in spend during working years (the ACA
    // subsidy only applies to retirement-era pre-Medicare years).
    const householdMonthlySpendForBudget = monthlySpendAt(
      y,
      events,
      assumptions.monthlyHouseholdSpend,
    );
    const householdAnnualSpendForBudget = householdMonthlySpendForBudget * 12;
    const baseSpendExMortgageForBudget =
      householdAnnualSpendForBudget - assumptions.mortgageMonthlyPI * 12;
    const escrowYearlyForBudget = assumptions.mortgageMonthlyEscrow * 12;
    const activeMortgagePIForBudget = mortgageMonthlyPI * 12;
    const mandatoryOutForBudget = working
      ? baseSpendExMortgageForBudget +
        escrowYearlyForBudget +
        assumptions.healthcareAnnual +
        activeMortgagePIForBudget +
        totalLoanPIThisYear
      : 0;

    // Discretionary outflows = the cash_flow funding-source amount. Bucket-
    // funded purchases pull from buckets directly (NOT from cashIn) so they
    // don't enter this term.
    const discretionaryOutForBudget = working ? cashFlowThisYear : 0;

    const budgetResult = reconcileBudget({
      cashIn: cashInForBudget,
      mandatoryOut: mandatoryOutForBudget,
      discretionaryOut: discretionaryOutForBudget,
      intended: {
        retirement: intendedAfterTaxRetirement,
        taxable: intendedTaxable,
        hysa: intendedHysa,
      },
    });

    // Apply the auto-reduction back to the bucket contributions. We trim
    // intended.retirement out of the AFTER-TAX portion only; pretax stays
    // untouched (already reflected by subtracting it from intended above).
    hysaContrib = working
      ? hysaContrib - budgetResult.autoReduction.fromHysa
      : hysaContrib;
    taxableContrib = working
      ? taxableContrib - budgetResult.autoReduction.fromTaxable
      : taxableContrib;
    retirementContrib = working
      ? retirementContrib - budgetResult.autoReduction.fromRetirement
      : retirementContrib;
    // Trim the Roth sleeve proportionally to the after-tax retirement trim
    // (so the sleeve totals stay consistent with the bucket contribution).
    if (
      working &&
      budgetResult.autoReduction.fromRetirement > 0 &&
      intendedAfterTaxRetirement > 0
    ) {
      const trimRatio =
        budgetResult.autoReduction.fromRetirement / intendedAfterTaxRetirement;
      const rothTrim = rothContribThisYear * trimRatio;
      rothContribThisYear = Math.max(0, rothContribThisYear - rothTrim);
    }
    // Slice 4: apply the same proportional trim to the per-rule timing
    // entries. Documented choice: we trim PROPORTIONALLY across all
    // contributing rules within a bucket rather than priority-ordered (e.g.,
    // trim later-month rules first). Proportional is simpler, deterministic,
    // and within engine noise; priority-ordered trimming is a future
    // refinement that would let users opt into "shave December rules first
    // when cash is tight".
    if (working) {
      const trimEntriesProportional = (
        entries: RuleTimingEntry[],
        trimAmount: number,
        eligible: (e: RuleTimingEntry) => boolean,
      ) => {
        if (trimAmount <= 0) return;
        const eligibleSum = entries
          .filter(eligible)
          .reduce((s, e) => s + e.dollars, 0);
        if (eligibleSum <= 0) return;
        // Cap the trim at what's actually present so we don't go negative.
        const trim = Math.min(trimAmount, eligibleSum);
        const trimRatio = trim / eligibleSum;
        for (const e of entries) {
          if (!eligible(e)) continue;
          e.dollars = Math.max(0, e.dollars * (1 - trimRatio));
        }
      };
      trimEntriesProportional(
        ruleTimingHysa,
        budgetResult.autoReduction.fromHysa,
        () => true,
      );
      trimEntriesProportional(
        ruleTimingTaxable,
        budgetResult.autoReduction.fromTaxable,
        () => true,
      );
      // After-tax retirement only; pretax 401(k) entries are sacrosanct.
      trimEntriesProportional(
        ruleTimingRetirement,
        budgetResult.autoReduction.fromRetirement,
        e => !e.isPretaxRetirement,
      );
    }

    if (budgetResult.overstretched) {
      eventNotes.push(
        `⚠ Overstretched: mandatory outflows ($${Math.round(mandatoryOutForBudget).toLocaleString()}) exceeded after-tax income. ` +
          `Net cash short by $${Math.round(-budgetResult.netCashSurplus).toLocaleString()}.`,
      );
    }

    // ── Build pools ──────────────────────────────────────────────────────
    let hysaPool = h + hysaContrib;
    let retirementPool = r + retirementContrib;
    let taxablePool = t + taxableContrib;
    // Track the Roth sleeve's pool position so withdrawals can take from
    // pretax first, Roth last. `roth` carries forward last year's Roth-only
    // total; new contributions to Roth this year add to it. Roth sleeve is
    // a SUBSET of `retirementPool`; we never let it exceed that.
    let rothPool = roth + rothContribThisYear;

    // ── Apply bucket pulls with spillover ────────────────────────────────
    let hysaPurchase = 0;
    let taxablePurchase = 0;
    let retirementPurchase = 0;
    const applyPull = (need: number, primary: 'hysa' | 'taxable' | 'retirement') => {
      let rem = need;
      for (const b of spilloverOrder(primary)) {
        if (rem <= 0) break;
        if (b === 'hysa') {
          const take = Math.min(rem, Math.max(0, hysaPool));
          hysaPool -= take;
          hysaPurchase += take;
          rem -= take;
        } else if (b === 'taxable') {
          const take = Math.min(rem, Math.max(0, taxablePool));
          taxablePool -= take;
          taxablePurchase += take;
          rem -= take;
        } else {
          const take = Math.min(rem, Math.max(0, retirementPool));
          retirementPool -= take;
          retirementPurchase += take;
          rem -= take;
        }
      }
      purchaseUnfunded += rem;
    };
    if (reqHysa > 0) applyPull(reqHysa, 'hysa');
    if (reqTaxable > 0) applyPull(reqTaxable, 'taxable');
    if (reqRetirement > 0) applyPull(reqRetirement, 'retirement');

    // ── Retirement spending withdrawals ──────────────────────────────────
    const activeMortgagePIYear = mortgageMonthlyPI * 12;
    // Recompute base spend using the effective monthly spend for this year
    // (so spending_change events propagate into the retirement/gap draw).
    // A gap-year `monthlySpendOverride` substitutes for the household-spend
    // input but does NOT replace escrow, healthcare, or active mortgage P&I —
    // those are real obligations that don't pause during a sabbatical.
    const householdMonthlySpend =
      onGap && gapOverride?.monthlySpendOverride !== undefined
        ? gapOverride.monthlySpendOverride
        : effectiveMonthlySpend;
    const householdAnnualSpend = householdMonthlySpend * 12;
    const effectiveBaseSpendExMortgage =
      householdAnnualSpend - assumptions.mortgageMonthlyPI * 12;
    // Slice 2.5 #3-4: Healthcare in retirement
    //   - Pre-65: ACA premium = healthcareAnnual × applicable-figure-pct.
    //     Tax depends on MAGI which depends on withdrawals which depend on
    //     spend which now includes ACA — chicken-and-egg. We use a 50% of
    //     healthcareAnnual placeholder for the spend pass, then recompute
    //     the actual subsidy after tax (reported in eventNotes; ~5% spend
    //     inaccuracy in transition years is the documented tradeoff).
    //   - 65+: IRMAA surcharge added on top of healthcareAnnual.
    const householdSizePre = assumptions.householdSize ??
      (assumptions.filingStatus === 'married' ? 2 : 1);
    void householdSizePre; // (used below in the actual refinement)
    // Retired (not gap, not working) — apply the ACA / IRMAA spend impacts.
    // Gap years usually have employer COBRA or short-term coverage, so we
    // don't apply ACA there; gap retains the unsubsidized `healthcareAnnual`.
    const isRetired = status === 'Retired';
    const acaPlaceholder =
      isRetired && age < 65 ? assumptions.healthcareAnnual * 0.5 : 0;
    const irmaaPlaceholder = isRetired && age >= 65
      ? // Estimate IRMAA from prior-year MAGI proxy — we use last year's row
        // since the current year's MAGI isn't computed yet. Falls back to 0
        // if no prior-year row.
        (rows.length > 0
          ? irmaaAnnualSurcharge(
              rows[rows.length - 1].tax.magi,
              assumptions.filingStatus as FilingStatus,
            )
          : 0)
      : 0;
    const healthcareThisYear =
      isRetired && age < 65
        ? acaPlaceholder
        : assumptions.healthcareAnnual + irmaaPlaceholder;
    // Working & gap years pay the unsubsidized `healthcareAnnual`. Retirees
    // age 65+ get Medicare baseline + IRMAA surcharge. Pre-Medicare retirees
    // pay the placeholder (refined after tax).
    const healthcareForSpend = isRetired ? healthcareThisYear : assumptions.healthcareAnnual;
    const effectiveBaseSpend =
      effectiveBaseSpendExMortgage +
      assumptions.mortgageMonthlyEscrow * 12 +
      healthcareForSpend;
    const baseSpendThisYear = effectiveBaseSpend + activeMortgagePIYear;
    // Benefit income during retirement/gap offsets spending need (but not
    // salary delta — in retirement, salary delta is a scenario input that
    // reflects a planned continued income stream only if the user modeled it
    // that way. Practically: treat benefit income as retirement offset, salary
    // as working-year only.)
    const retirementOffset = !working ? benefitIncomeAnnual : 0;
    // During retirement / gap years, loan P&I is part of the spend we need
    // to cover from savings. During working years we already deducted it
    // from contributions above, so it's not part of spendThisYear.
    const loanPIForSpend = !working ? totalLoanPIThisYear : 0;
    const spendThisYear = Math.max(
      0,
      baseSpendThisYear + loanPIForSpend - retirementOffset,
    );

    let taxableWd = 0;
    let hysaWd = 0;
    let retirementWd = 0;
    let rothWd = 0; // Roth-sleeve portion of retirementWd (tax-free)
    if (!working) {
      let need = spendThisYear;
      taxableWd = Math.min(need, Math.max(0, taxablePool));
      taxablePool -= taxableWd;
      need -= taxableWd;
      hysaWd = Math.min(need, Math.max(0, hysaPool));
      hysaPool -= hysaWd;
      need -= hysaWd;
      // Retirement withdrawal: PRETAX sleeve first (taxed as ordinary
      // income), then ROTH sleeve last (tax-free). Roth comes last so
      // tax-free growth keeps compounding as long as possible.
      const pretaxAvailable = Math.max(0, retirementPool - rothPool);
      const fromPretax = Math.min(need, pretaxAvailable);
      retirementPool -= fromPretax;
      need -= fromPretax;
      const fromRoth = Math.min(need, Math.max(0, rothPool));
      retirementPool -= fromRoth;
      rothPool -= fromRoth;
      retirementWd = fromPretax + fromRoth;
      rothWd = fromRoth;
    }

    // ── Amortize mortgage ────────────────────────────────────────────────
    const begMortgage = mortgageBal;
    let endMortgage: number;
    if (begMortgage <= 0 || mortgageRate <= 0) {
      endMortgage = Math.max(0, begMortgage - mortgageMonthlyPI * 12);
    } else {
      const m = mortgageRate / 12;
      endMortgage = Math.max(
        0,
        begMortgage * Math.pow(1 + m, 12) -
          (mortgageMonthlyPI * (Math.pow(1 + m, 12) - 1)) / m,
      );
    }
    const principalPaid = begMortgage - endMortgage;
    const totalPaid = Math.min(
      mortgageMonthlyPI * 12,
      begMortgage + begMortgage * mortgageRate,
    );
    const interestPaid =
      begMortgage > 0 ? Math.max(0, totalPaid - principalPaid) : 0;
    mortgageSchedule.push({
      yearOffset: y,
      calendarYear,
      age,
      begBalance: begMortgage,
      interestPaid,
      principalPaid,
      endBalance: endMortgage,
    });
    mortgageBal = endMortgage;

    // ── Annual tax breakdown ─────────────────────────────────────────────
    //
    // Build the tax-engine input from this year's income and withdrawals:
    //   - Wages: salary during working years only, REDUCED by pretax 401(k)
    //     deferrals (slice 2: cap-aware pretax sleeve total)
    //   - "Other ordinary": pretax 401(k) withdrawals + Roth conversions +
    //     pension. Roth-sleeve withdrawals are EXCLUDED (tax-free).
    //   - LTCG: a fraction of taxable-bucket withdrawals (using a 40% gain
    //     default — realistic for a long-held portfolio, configurable later)
    //   - SS benefits: from any active social_security effects
    //   - NII: LTCG portion for simplicity (dividends/interest not separately modeled)
    const ssBenefitsThisYear = activeNow
      .filter(x => x.kind === 'ss')
      .reduce((s, x) => s + x.monthly * 12, 0);
    const pensionThisYear = activeNow
      .filter(x => x.kind === 'pension')
      .reduce((s, x) => s + x.monthly * 12, 0);
    // (rothConversionAmount was computed earlier from the events.)
    const rothConversionsThisYear = rothConversionAmount;
    const gainFraction = 0.4; // conservative default; TODO: track cost basis
    const ltcgThisYear = taxableWd * gainFraction;
    // Only the PRETAX portion of retirement withdrawals counts as ordinary
    // income — Roth-sleeve withdrawals are tax-free.
    const pretaxRetirementWd = Math.max(0, retirementWd - rothWd);
    const ordinaryExtra =
      pretaxRetirementWd +
      rothConversionsThisYear +
      pensionThisYear;
    // Pretax 401(k) employee deferrals reduce taxable wages. Slice 2:
    // use the cap-aware pretax sleeve total (after 402(g)/415(c) clamps),
    // not the user's intended figure — capped overage stays as W-2 wages.
    const pretaxRetirementContrib = working
      ? capResult
        ? capResult.pretaxTotal *
          (baselineRetirement > 0 ? effectiveContribR / baselineRetirement : 0)
        : effectiveContribR
      : 0;
    const wagesThisYear = working ? Math.max(0, effectiveSalary - pretaxRetirementContrib) : 0;
    const taxResult = computeAnnualTax({
      status: assumptions.filingStatus as FilingStatus,
      wages: wagesThisYear,
      otherOrdinary: ordinaryExtra,
      ltcg: ltcgThisYear,
      ssBenefits: ssBenefitsThisYear,
      netInvestmentIncome: ltcgThisYear,
      stateRate: assumptions.stateTaxRate,
    });
    const irmaa = age >= 65 ? irmaaAnnualSurcharge(taxResult.magi, assumptions.filingStatus as FilingStatus) : 0;
    // RMD computed from pretax-sleeve balance (Roth IRAs / Roth 401(k)s
    // post-SECURE 2.0 do NOT have RMDs, but legacy Roth 401(k) RMDs were
    // eliminated in 2024 — so the RMD calc looks only at the pretax sleeve).
    const rmd = requiredMinDistribution(Math.max(0, retirementPool - rothPool) + retirementWd, age);

    // Slice 2.5 #1: Roth-conversion tax cost via marginal-bracket delta.
    // We just ran computeAnnualTax with the conversion in ordinaryExtra; now
    // run it again WITHOUT the conversion and use the difference as the true
    // marginal cost. Pull that from HYSA via a post-pool deduction (the spend
    // pass already ran, and reqHysa was applied earlier — so we deduct from
    // hysaPool directly before growth).
    let rothConversionTaxCost = 0;
    if (rothConversionsThisYear > 0) {
      const taxResultNoConversion = computeAnnualTax({
        status: assumptions.filingStatus as FilingStatus,
        wages: wagesThisYear,
        otherOrdinary: Math.max(0, ordinaryExtra - rothConversionsThisYear),
        ltcg: ltcgThisYear,
        ssBenefits: ssBenefitsThisYear,
        netInvestmentIncome: ltcgThisYear,
        stateRate: assumptions.stateTaxRate,
      });
      rothConversionTaxCost = Math.max(
        0,
        (taxResult.federalOrdinaryTax + taxResult.stateTax) -
          (taxResultNoConversion.federalOrdinaryTax + taxResultNoConversion.stateTax),
      );
      // Pull tax cost from HYSA. If HYSA can't cover it, spill into taxable.
      const fromHysa = Math.min(hysaPool, rothConversionTaxCost);
      hysaPool -= fromHysa;
      let rem = rothConversionTaxCost - fromHysa;
      if (rem > 0) {
        const fromTaxable = Math.min(taxablePool, rem);
        taxablePool -= fromTaxable;
        rem -= fromTaxable;
      }
      // Roth conversion: principal moves from pretax sleeve to Roth sleeve
      // within the retirement bucket. Total bucket balance unchanged; sleeve
      // mix shifts. (If pretax has less than the conversion amount, we cap
      // at what's available — the user can't convert what doesn't exist.)
      const pretaxAvail = Math.max(0, retirementPool - rothPool);
      const actualConverted = Math.min(rothConversionsThisYear, pretaxAvail);
      rothPool += actualConverted;
    }

    // Slice 2.5 #3: ACA premium pre-Medicare. For age 60-64 (post-retire,
    // pre-Medicare) the user pays a fraction of premium based on MAGI.
    // Approximation: estimate ACA premium as healthcareAnnual × applicable%
    // (the legacy `healthcareAnnual` is the "all-cash" no-subsidy figure).
    // Iterative-fixed-point limitation: tax depends on withdrawals; ACA
    // depends on tax (MAGI); withdrawals depend on spend (which now includes
    // ACA). Slice 2.5 doesn't iterate to convergence — we recompute ACA on
    // the post-spend MAGI and report it as informational rather than feeding
    // back into the spend pass. This produces ~5% inaccuracy in transition
    // years; future slice could iterate. The number reported is honest.
    const householdSize =
      assumptions.householdSize ??
      (assumptions.filingStatus === 'married' ? 2 : 1);
    const acaPct =
      isRetired && age < 65
        ? acaApplicableFigurePct(taxResult.magi, householdSize)
        : 0;
    // Estimate the user's actual cost for healthcare under ACA: their
    // applicable-figure share of the benchmark cost. (The benchmark
    // approximation here is `healthcareAnnual` itself, treating it as the
    // unsubsidized amount.) Subsidy = healthcareAnnual − userCost.
    const acaUserCost = isRetired && age < 65 ? assumptions.healthcareAnnual * acaPct : 0;
    const acaSubsidy = isRetired && age < 65
      ? Math.max(0, assumptions.healthcareAnnual - acaUserCost)
      : 0;

    // Slice 2.5 #2: RMD enforcement. At age 73+ if our voluntary
    // retirement withdrawal didn't meet the RMD, force the rest. The excess
    // (above the user's spending need) gets DEPOSITED into the taxable
    // bucket — the user was forced to liquidate but didn't need to spend.
    // Tax was already computed above using `pretaxRetirementWd`; we add the
    // forced overage to ordinary income via a re-compute below.
    let rmdForced = 0;
    if (rmd > 0 && pretaxRetirementWd < rmd) {
      const shortfall = rmd - pretaxRetirementWd;
      const pretaxAvail = Math.max(0, retirementPool - rothPool);
      rmdForced = Math.min(shortfall, pretaxAvail);
      retirementPool -= rmdForced;
      taxablePool += rmdForced; // the dollars survive — moved to taxable
      eventNotes.push(
        `RMD: ${Math.round(rmd).toLocaleString()} required; ` +
          `${Math.round(pretaxRetirementWd).toLocaleString()} taken from 401(k); ` +
          `excess ${Math.round(rmdForced).toLocaleString()} deposited to taxable.`,
      );
    }
    // If RMD forced extra ordinary income, recompute tax to reflect it.
    // (Cheap — at most one extra computeAnnualTax per RMD-affected year.)
    let finalTaxResult = taxResult;
    if (rmdForced > 0) {
      finalTaxResult = computeAnnualTax({
        status: assumptions.filingStatus as FilingStatus,
        wages: wagesThisYear,
        otherOrdinary: ordinaryExtra + rmdForced,
        ltcg: ltcgThisYear,
        ssBenefits: ssBenefitsThisYear,
        netInvestmentIncome: ltcgThisYear,
        stateRate: assumptions.stateTaxRate,
      });
    }
    // Surface ACA subsidy in eventNotes when relevant.
    if (acaSubsidy > 0) {
      eventNotes.push(
        `ACA: subsidy ${Math.round(acaSubsidy).toLocaleString()} this year ` +
          `(your share ${Math.round(acaUserCost).toLocaleString()} of ${Math.round(assumptions.healthcareAnnual).toLocaleString()}).`,
      );
    }
    // Surface IRMAA when it bites.
    if (irmaa > 0) {
      eventNotes.push(`IRMAA: +${Math.round(irmaa).toLocaleString()}/yr surcharge.`);
    }

    // ── Grow buckets and appreciate home ─────────────────────────────────
    // Growth runs LAST so all in-year deductions (Roth-conversion tax cost,
    // RMD reroute) hit the pre-growth pool — i.e., we don't grow dollars
    // that left the bucket this year.
    //
    // Slice 4 — time-weighted contributions:
    //   end = (pool − rule_total) × (1 + r)        // prior balance + non-rule
    //                                              //   inflows − pulls grow
    //                                              //   for a full year
    //       + Σ(rule_dollars × schedule_factor)    // each rule's dollars
    //                                              //   grow per their schedule
    //
    // The "non-rule portion" includes salary delta, windfalls, refinance
    // cash-out, home_sale proceeds routed to taxable/HYSA, employer match,
    // and 401(k)-loan repayments. Continuing to treat those as start-of-year
    // is a documented simplification: those flows don't have an authored
    // schedule, and "Jan-equivalent" is the conservative default the engine
    // already used for them.
    const sumRuleEntries = (entries: RuleTimingEntry[]) =>
      entries.reduce((s, e) => s + e.dollars, 0);
    const sumRuleEntriesWeighted = (entries: RuleTimingEntry[]) =>
      entries.reduce((s, e) => s + e.dollars * e.factor, 0);
    const ruleSumHysa = sumRuleEntries(ruleTimingHysa);
    const ruleSumTaxable = sumRuleEntries(ruleTimingTaxable);
    const ruleSumRetirement = sumRuleEntries(ruleTimingRetirement);
    const hysaEnd = growWithRules(
      hysaPool,
      assumptions.hysaRealReturn,
      ruleSumHysa,
      sumRuleEntriesWeighted(ruleTimingHysa),
    );
    const retirementEnd = growWithRules(
      retirementPool,
      assumptions.stockRealReturn,
      ruleSumRetirement,
      sumRuleEntriesWeighted(ruleTimingRetirement),
    );
    const taxableEnd = growWithRules(
      taxablePool,
      assumptions.stockRealReturn,
      ruleSumTaxable,
      sumRuleEntriesWeighted(ruleTimingTaxable),
    );
    // Roth sleeve grows at the same real-return rate as the rest of the
    // retirement bucket (it lives inside it), and is bounded by the parent.
    // For time-weighting, the Roth sleeve uses the subset of retirement-bucket
    // rule entries whose taxTreatment lands in a Roth sleeve: roth_401k,
    // mega_backdoor_to_roth, roth_ira. `rothPool` already includes (last
    // year's Roth carry-forward + this year's Roth contribution); we
    // time-weight just the contribution component.
    let rothRuleSum = 0;
    let rothRuleWeighted = 0;
    if (capResult && working && assumptions.contributionRules) {
      const rothTreatments = new Set([
        'roth_401k',
        'mega_backdoor_to_roth',
        'roth_ira',
      ]);
      for (const rule of assumptions.contributionRules) {
        if (!rule.taxTreatment || !rothTreatments.has(rule.taxTreatment)) continue;
        const entry = ruleTimingRetirement.find(e => e.ruleId === rule.id);
        if (!entry) continue;
        rothRuleSum += entry.dollars;
        rothRuleWeighted += entry.dollars * entry.factor;
      }
    }
    const rothEnd = Math.min(
      retirementEnd,
      growWithRules(
        rothPool,
        assumptions.stockRealReturn,
        rothRuleSum,
        rothRuleWeighted,
      ),
    );
    homeValue = homeValue * (1 + assumptions.homeRealAppreciation);

    const investable = hysaEnd + retirementEnd + taxableEnd;
    const totalLoanBalanceAtYearEnd = loanActivityThisYear.reduce(
      (s, l) => s + l.balance,
      0,
    );
    // Net worth nets out every liability — the primary mortgage plus any
    // outstanding non-mortgage loans. (401(k) loans are NOT subtracted: the
    // money is "owed to yourself" and already accounted for by being
    // temporarily outside the retirement bucket.)
    const nonMortgageLiability = loanActivityThisYear
      .filter(l => l.loanKind !== 'retirement_401k')
      .reduce((s, l) => s + l.balance, 0);
    const netWorth = investable + homeValue - mortgageBal - nonMortgageLiability;
    const swrIncome = investable * assumptions.swr;

    // ── Build the YearBudget snapshot ────────────────────────────────────
    // The "intended" we report includes the user's full intended retirement
    // contribution (pretax + after-tax), so the UI can show both sides
    // honestly. The reconciler only operated on the after-tax portion, so
    // pretax dollars in `intendedContributions.retirement` always equal
    // `actualContributions.retirement`'s pretax portion (no auto-reduction
    // could have applied to them by design).
    // The reported `intendedContributions.retirement` is the user's full
    // intended retirement contribution = pretax (untouchable) + after-tax
    // (trimmable) + 401(k) loan repayments (wash; mandatory cash flow that
    // returns principal to the bucket). Same composition for `actual`,
    // with the after-tax portion replaced by the post-trim figure.
    const budget: YearBudget = {
      cashIn: cashInForBudget,
      mandatoryOut: mandatoryOutForBudget,
      discretionaryOut: discretionaryOutForBudget,
      intendedContributions: {
        retirement:
          pretaxRetirementContribForBudget +
          intendedAfterTaxRetirement +
          total401kRepaidThisYear,
        taxable: intendedTaxable,
        hysa: intendedHysa,
        employerMatch: working ? employerMatchThisYear : 0,
      },
      actualContributions: {
        retirement:
          pretaxRetirementContribForBudget +
          budgetResult.actual.retirement +
          total401kRepaidThisYear,
        taxable: budgetResult.actual.taxable,
        hysa: budgetResult.actual.hysa,
        employerMatch: working ? employerMatchThisYear : 0,
      },
      autoReduction: budgetResult.autoReduction,
      overstretched: budgetResult.overstretched,
      netCashSurplus: budgetResult.netCashSurplus,
    };

    rows.push({
      yearOffset: y,
      calendarYear,
      age,
      status,
      hysa: {
        beg: h,
        contrib: hysaContrib,
        withdraw: hysaWd,
        purchase: hysaPurchase,
        end: hysaEnd,
      },
      retirement: {
        beg: r,
        contrib: retirementContrib,
        withdraw: retirementWd,
        purchase: retirementPurchase,
        end: retirementEnd,
      },
      taxable: {
        beg: t,
        contrib: taxableContrib,
        withdraw: taxableWd,
        purchase: taxablePurchase,
        end: taxableEnd,
      },
      roth: {
        beg: roth,
        contrib: rothContribThisYear,
        withdraw: rothWd,
        purchase: 0, // Roth sleeve isn't directly used for purchase funding (yet)
        end: rothEnd,
      },
      mortgageBalance: mortgageBal,
      homeValue,
      investable,
      netWorth,
      swrIncome,
      requiredSpend: spendThisYear,
      surplus: swrIncome - spendThisYear,
      purchaseTotal: hysaPurchase + taxablePurchase + retirementPurchase,
      purchasePlanned,
      purchaseUnfunded,
      purchaseFromRetirement: retirementPurchase,
      extraIncome: extraIncomeAnnual,
      loans: loanActivityThisYear,
      loanPIYear: totalLoanPIThisYear,
      totalLoanBalance: totalLoanBalanceAtYearEnd,
      tax: { ...finalTaxResult, irmaaSurcharge: irmaa, rmd },
      budget,
      overstretched: budget.overstretched,
      eventNotes,
    });

    // Drop fully-paid loans so they don't get amortized again.
    for (let i = activeLoans.length - 1; i >= 0; i--) {
      if (activeLoans[i].balance <= 0.01) activeLoans.splice(i, 1);
    }

    h = hysaEnd;
    r = retirementEnd;
    t = taxableEnd;
    roth = rothEnd;
  }

  return {
    years: rows,
    mortgage: mortgageSchedule,
    requiredSpend: baseSpendForNestEgg,
    requiredNestEgg,
  };
}

/** Slice 4 — bucket end-of-year value with time-weighted rule contributions.
 *
 *  The non-rule portion of the pool (prior balance + non-rule inflows minus
 *  in-year deductions) gets the full (1 + r) growth. The rule portion is
 *  replaced with the per-schedule weighted sum.
 *
 *  Defensive notes:
 *    - If `ruleSum > pool` (e.g. trimmed too aggressively or non-rule
 *      deductions ate into the rule's nominal share) the non-rule portion
 *      goes negative and the formula still produces the correct EOY value
 *      because both terms net out symmetrically. We clamp at 0 only at
 *      return so the bucket never reports a negative balance.
 *    - When `ruleSum === 0` (legacy fallback or no working-year rules) this
 *      reduces to `pool * (1 + r)` — i.e., identical to the legacy `grow()`.
 */
function growWithRules(
  pool: number,
  rate: number,
  ruleSum: number,
  ruleWeighted: number,
): number {
  if (ruleSum === 0) return Math.max(0, pool * (1 + rate));
  const nonRule = pool - ruleSum;
  return Math.max(0, nonRule * (1 + rate) + ruleWeighted);
}

function spilloverOrder(
  source: 'hysa' | 'taxable' | 'retirement',
): readonly ('hysa' | 'taxable' | 'retirement')[] {
  switch (source) {
    case 'hysa':
      return ['hysa', 'taxable', 'retirement'] as const;
    case 'taxable':
      return ['taxable', 'hysa', 'retirement'] as const;
    case 'retirement':
      return ['retirement', 'taxable', 'hysa'] as const;
  }
}

// ── Snapshot helpers (unchanged from v1) ────────────────────────────────────

function snapshotSortKey(s: Snapshot): string {
  return `${s.date}T${s.createdAt}`;
}

export function sortedSnapshotsAsc(snapshots: Snapshot[]): Snapshot[] {
  return [...snapshots].sort((a, b) =>
    snapshotSortKey(a) < snapshotSortKey(b) ? -1 : 1,
  );
}

export function latestBalances(snapshots: Snapshot[]): Record<string, number> {
  const sorted = sortedSnapshotsAsc(snapshots);
  const bal: Record<string, number> = {};
  for (const s of sorted) {
    for (const [k, v] of Object.entries(s.balances)) {
      bal[k] = v;
    }
  }
  return bal;
}

export function accountHistory(
  snapshots: Snapshot[],
  accounts: Account[],
): Record<string, { date: string; value: number }[]> {
  const sorted = sortedSnapshotsAsc(snapshots);
  const carry: Record<string, number> = {};
  const out: Record<string, { date: string; value: number }[]> = {};
  for (const a of accounts) out[a.id] = [];
  for (const s of sorted) {
    Object.assign(carry, s.balances);
    for (const a of accounts) {
      out[a.id].push({ date: s.date, value: carry[a.id] ?? 0 });
    }
  }
  return out;
}

export function accountTouches(
  snapshots: Snapshot[],
  accounts: Account[],
): Record<string, { date: string; value: number; createdAt: string }[]> {
  const sorted = sortedSnapshotsAsc(snapshots);
  const out: Record<string, { date: string; value: number; createdAt: string }[]> = {};
  for (const a of accounts) out[a.id] = [];
  for (const s of sorted) {
    for (const a of accounts) {
      if (Object.prototype.hasOwnProperty.call(s.balances, a.id)) {
        out[a.id].push({ date: s.date, value: s.balances[a.id], createdAt: s.createdAt });
      }
    }
  }
  return out;
}

// ── Slice 6: friendly problem surfacing ─────────────────────────────────────
//
// `getProjectionProblems` is a pure read helper that scans an already-built
// projection and surfaces the years where Cairn made (or had to make) a
// non-obvious judgement call: the budget overstretched, an IRS cap clipped a
// contribution, an RMD forced a withdrawal, a bucket ran dry, net worth
// turned negative, or planned spending couldn't be funded.
//
// It performs NO new math — every signal it returns is already on the
// `ProjectionYear` (via `eventNotes`, `budget.autoReduction`, `tax.rmd`,
// `purchaseUnfunded`, etc.). This keeps the engine the single source of
// truth and lets the UI render whatever subset it needs in a stable order.
//
// Severities are assigned to keep the visual language calm:
//   - `note`     → informational (RMD that the engine handled gracefully)
//   - `warning`  → user attention recommended (cap hit, overstretched budget)
//   - `critical` → projection model is reaching the edges of its usefulness
//                  (bucket exhausted, NW negative, planned spending unfunded)
// All three are rendered with muted/warm colors in the UI — never red alerts.

export type ProblemSeverity = 'note' | 'warning' | 'critical';

export type ProblemKind =
  | 'overstretched'
  | 'cap_hit'
  | 'rmd'
  | 'bucket_exhausted'
  | 'net_worth_negative'
  | 'unfunded';

/** An action the user can take in response to a Problem — typically a hash
 *  link to the page where they can act on the recommendation. Rendered as a
 *  link/button in `ProblemPanel`. Every suggestion-bearing problem should
 *  have at least one. */
export interface ProblemAction {
  label: string;
  href: string;
}

export interface Problem {
  /** Stable identifier — `${kind}:${calendarYear}` plus an optional discriminator. */
  id: string;
  kind: ProblemKind;
  severity: ProblemSeverity;
  /** First calendar year where this problem appeared. */
  calendarYear: number;
  /** Last calendar year of the run (inclusive) — same as `calendarYear` for
   *  one-off events, or the end of a contiguous streak for grouped problems. */
  endCalendarYear: number;
  /** First year-offset the problem appeared (so the UI can seek). */
  yearOffset: number;
  /** Human-readable, plain-language title. */
  title: string;
  /** One-paragraph plain-language explanation, safe to render verbatim. */
  body: string;
  /** Optional in-app actions the user can take to address the problem.
   *  Rendered as links by the UI. Every suggestion in the body should map
   *  to a corresponding action here so users don't have to hunt. */
  actions?: ProblemAction[];
  /** Glossary term ids referenced by `title` + `body`. The UI uses this to
   *  render a "Terms in this note" chip row that opens the glossary panel —
   *  body strings can't contain React components, so we surface jargon refs
   *  as data instead. Populated by `getProjectionProblems`. */
  termRefs?: string[];
}

/** Rolls each `ProjectionYear` into a flat `YearProblems` map so the table /
 *  chart can render badges per row without re-scanning the whole projection. */
export interface YearProblems {
  yearOffset: number;
  calendarYear: number;
  /** Highest severity active in this year (drives badge color). */
  severity: ProblemSeverity | null;
  kinds: ProblemKind[];
  /** Plain-language tooltip text — one bullet per active signal. */
  tooltip: string;
}

const SEVERITY_RANK: Record<ProblemSeverity, number> = {
  note: 0,
  warning: 1,
  critical: 2,
};

function maxSeverity(a: ProblemSeverity | null, b: ProblemSeverity): ProblemSeverity {
  if (a == null) return b;
  return SEVERITY_RANK[b] > SEVERITY_RANK[a] ? b : a;
}

/** Per-year, per-kind signal extraction. Pure: looks only at the row. */
function detectYearKinds(y: ProjectionYear): {
  kind: ProblemKind;
  severity: ProblemSeverity;
  note: string;
}[] {
  const out: { kind: ProblemKind; severity: ProblemSeverity; note: string }[] = [];
  // 1. Budget overstretched
  if (y.overstretched) {
    out.push({
      kind: 'overstretched',
      severity: 'warning',
      note: `Mandatory expenses exceeded after-tax income; Cairn auto-trimmed savings to balance.`,
    });
  } else if ((y.budget?.autoReduction?.total ?? 0) > 0.5) {
    // Auto-reduction without overstretched is a softer signal — savings got
    // trimmed but the year still balanced. Same kind, milder copy.
    out.push({
      kind: 'overstretched',
      severity: 'warning',
      note: `Cairn trimmed savings to balance the year's cash flow.`,
    });
  }
  // 2. Contribution caps hit (look in eventNotes — engine already records them).
  for (const note of y.eventNotes) {
    if (/capped at/i.test(note)) {
      out.push({ kind: 'cap_hit', severity: 'warning', note });
    }
  }
  // 3. RMD forced. The engine surfaces "RMD: …" notes only when an RMD
  //    actually applied. Treated as a note (informational), not a warning —
  //    Cairn handled it gracefully and the dollars stayed in the household.
  if ((y.tax?.rmd ?? 0) > 0.5 && y.eventNotes.some(n => n.startsWith('RMD:'))) {
    const rmdNote = y.eventNotes.find(n => n.startsWith('RMD:')) ?? '';
    out.push({ kind: 'rmd', severity: 'note', note: rmdNote });
  }
  // 4. Bucket exhausted: any investable bucket runs to ~0 with non-trivial
  //    prior year balance. Critical because all subsequent withdrawals draw
  //    from a different bucket (potentially with tax/penalty consequences).
  //    We only flag the *first* exhaustion year; downstream consumers group.
  // (Detection happens in the cross-year scan below — this function returns
  //  per-row signals only.)
  // 5. Net-worth negative: the projection is past its useful horizon. Critical.
  if (y.netWorth < 0) {
    out.push({
      kind: 'net_worth_negative',
      severity: 'critical',
      note: `Projected net worth dipped below zero — the model is past its useful range here.`,
    });
  }
  // 6. Unfunded purchases: planned outflows that no bucket could cover.
  if ((y.purchaseUnfunded ?? 0) > 0.5) {
    out.push({
      kind: 'unfunded',
      severity: 'critical',
      note: `Some planned spending couldn't be funded by any bucket this year.`,
    });
  }
  return out;
}

interface BucketExhaustionSignal {
  bucket: 'hysa' | 'taxable' | 'retirement';
  yearOffset: number;
  calendarYear: number;
  /** End-balance the year before exhaustion (used to suppress trivially-empty buckets). */
  priorEnd: number;
}

function detectBucketExhaustions(years: ProjectionYear[]): BucketExhaustionSignal[] {
  const out: BucketExhaustionSignal[] = [];
  const buckets: ('hysa' | 'taxable' | 'retirement')[] = ['hysa', 'taxable', 'retirement'];
  for (const b of buckets) {
    let exhausted = false;
    // Treat the row immediately before `years[0]` as having an unknown
    // prior balance — only flag as "newly exhausted" when we have an
    // actual prior row showing real money, otherwise we'd surface every
    // bucket that simply starts empty (e.g., a synthetic test year with
    // all-zero balances, or a profile that never funded a bucket).
    for (let i = 1; i < years.length; i++) {
      const y = years[i];
      const end = Math.max(0, y[b].end);
      const prevEnd = Math.max(0, years[i - 1][b].end);
      if (!exhausted && end < 1 && prevEnd >= 1_000) {
        out.push({
          bucket: b,
          yearOffset: y.yearOffset,
          calendarYear: y.calendarYear,
          priorEnd: prevEnd,
        });
        exhausted = true;
      } else if (exhausted && end >= 1) {
        // Refilled (e.g., from an RMD reroute or windfall) — re-arm so a
        // later second exhaustion still surfaces.
        exhausted = false;
      }
    }
  }
  return out;
}

const BUCKET_LABEL: Record<'hysa' | 'taxable' | 'retirement', string> = {
  hysa: 'HYSA',
  taxable: 'taxable bucket',
  retirement: '401(k)',
};

/** Group consecutive same-kind year-level signals into a single Problem. */
function groupConsecutive(
  signals: {
    yearOffset: number;
    calendarYear: number;
    kind: ProblemKind;
    severity: ProblemSeverity;
    note: string;
  }[],
): Problem[] {
  if (signals.length === 0) return [];
  const sorted = [...signals].sort(
    (a, b) => a.kind.localeCompare(b.kind) || a.yearOffset - b.yearOffset,
  );
  const out: Problem[] = [];
  let run: typeof sorted | null = null;
  const flush = () => {
    if (!run || run.length === 0) return;
    const first = run[0];
    const last = run[run.length - 1];
    const single = first.calendarYear === last.calendarYear;
    let title: string;
    let body: string;
    let actions: ProblemAction[] | undefined;
    switch (first.kind) {
      case 'overstretched':
        title = single
          ? `Heads up — your budget was tight in ${first.calendarYear}`
          : `Your budget was tight ${run.length} years between ${first.calendarYear} and ${last.calendarYear}`;
        body =
          `Mandatory expenses (housing, healthcare, mortgage P&I, taxes) edged past after-tax income, ` +
          `so Cairn auto-trimmed your taxable and HYSA contributions to balance the year. ` +
          `If you'd like to restore your savings rate, consider lowering monthly spend or pushing your retirement age out a year.`;
        actions = [
          { label: 'Adjust assumptions', href: '#assumptions' },
          { label: 'Review scenarios', href: '#scenarios' },
        ];
        break;
      case 'cap_hit':
        title = single
          ? `You hit an IRS contribution cap in ${first.calendarYear}`
          : `You hit an IRS contribution cap in ${run.length} years (${first.calendarYear}–${last.calendarYear})`;
        body =
          `Some of your intended contributions exceeded the annual IRS limit and didn't land. ` +
          `Consider routing the spillover to a Roth IRA, a mega-backdoor Roth (if your employer plan supports it), or a taxable brokerage account.`;
        actions = [{ label: 'Manage contribution rules', href: '#investments' }];
        break;
      case 'rmd':
        title = single
          ? `RMD started in ${first.calendarYear}`
          : `RMDs in effect ${first.calendarYear}–${last.calendarYear}`;
        body =
          `At age 73, the IRS requires a minimum distribution from your pretax 401(k). ` +
          `Cairn took it. The portion above your spending need was deposited to your taxable bucket — the dollars stayed in the household.`;
        // RMD is informational — engine handled it; no user action required.
        break;
      case 'unfunded':
        title = single
          ? `Some planned spending couldn't be funded in ${first.calendarYear}`
          : `Planned spending exceeded available funds in ${run.length} years (${first.calendarYear}–${last.calendarYear})`;
        body =
          `One or more outflows this year had no bucket with enough cash to cover them. ` +
          `Consider lowering the purchase amount, deferring it to a later year, or routing it through cash flow / loan funding.`;
        actions = [{ label: 'Edit scenario events', href: '#scenarios' }];
        break;
      case 'net_worth_negative':
        title = single
          ? `Net worth turns negative in ${first.calendarYear}`
          : `Net worth stays negative from ${first.calendarYear} onward`;
        body =
          `The projection extends past where the model is realistic — long-horizon assumptions compound and the picture stops being meaningful. ` +
          `Consider shortening your horizon or revisiting your spending and return assumptions.`;
        actions = [
          { label: 'Adjust assumptions', href: '#assumptions' },
          { label: 'Try a different scenario', href: '#scenarios' },
        ];
        break;
      case 'bucket_exhausted':
        // Bucket exhaustion is added separately (carries bucket label).
        title = `Bucket exhausted in ${first.calendarYear}`;
        body = first.note;
        actions = [
          { label: 'Adjust assumptions', href: '#assumptions' },
          { label: 'Review scenarios', href: '#scenarios' },
        ];
        break;
    }
    out.push({
      id: `${first.kind}:${first.calendarYear}${single ? '' : `-${last.calendarYear}`}`,
      kind: first.kind,
      severity: run.reduce<ProblemSeverity>(
        (sev, s) => maxSeverity(sev, s.severity)!,
        run[0].severity,
      ),
      calendarYear: first.calendarYear,
      endCalendarYear: last.calendarYear,
      yearOffset: first.yearOffset,
      title,
      body,
      actions,
    });
  };
  for (const s of sorted) {
    if (
      run &&
      run[run.length - 1].kind === s.kind &&
      s.calendarYear - run[run.length - 1].calendarYear <= 1
    ) {
      run.push(s);
    } else {
      flush();
      run = [s];
    }
  }
  flush();
  return out;
}

/**
 * Lightweight regex-based scanner that maps phrases in a Problem's title +
 * body to known glossary term ids. Keep these patterns conservative — false
 * positives produce a chip pointing somewhere unhelpful; false negatives are
 * just a missed teaching moment. Order matters: longer / more specific
 * patterns first so e.g. "Roth IRA" beats bare "Roth".
 *
 * IMPORTANT: glossary ids referenced here MUST exist in src/glossary/terms.ts.
 * The Problem-side scanner doesn't import the glossary to keep the engine
 * dependency-free; cross-validation happens via the test suite.
 */
const PROBLEM_TERM_PATTERNS: { id: string; re: RegExp }[] = [
  { id: 'mega_backdoor', re: /\bmega[- ]backdoor\b/i },
  { id: 'roth_ira', re: /\bRoth IRA\b/i },
  { id: 'roth_401k', re: /\bRoth 401\(k\)\b/i },
  { id: 'irs_402g', re: /402\(?g\)?/i },
  { id: 'irs_415c', re: /415\(?c\)?/i },
  { id: 'rmd', re: /\b(RMD|required minimum distribution)s?\b/i },
  { id: 'irmaa', re: /\bIRMAA\b/i },
  { id: 'aca', re: /\b(ACA|Affordable Care Act)\b/i },
  { id: 'magi', re: /\bMAGI\b/i },
  { id: 'pretax_401k', re: /\bpretax 401\(k\)/i },
  { id: '401k', re: /\b401\(k\)/i },
  { id: 'taxable_brokerage', re: /\btaxable (?:bucket|brokerage)\b/i },
  { id: 'hysa', re: /\bHYSA\b/i },
  { id: 'roth_conversion', re: /\bRoth conversion\b/i },
  { id: 'ltcg', re: /\b(long-term capital gains|LTCG)\b/i },
  { id: 'social_security', re: /\bSocial Security\b/i },
  { id: 'overstretched', re: /\bauto-trim(?:med)?\b/i },
  { id: 'mortgage_pi', re: /\bP&I\b/i },
];

/** Returns the set of glossary term ids referenced by a problem's title + body
 *  (deduplicated, in pattern-list order so chip ordering is deterministic). */
function extractTermRefs(title: string, body: string): string[] {
  const hay = `${title}\n${body}`;
  const ids: string[] = [];
  for (const { id, re } of PROBLEM_TERM_PATTERNS) {
    if (re.test(hay) && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** Returns a prioritised, plain-language list of problems detected across
 *  the projection. Pass `limit` to cap the result (defaults to all). */
export function getProjectionProblems(
  years: ProjectionYear[],
  opts?: { limit?: number },
): Problem[] {
  const yearSignals: {
    yearOffset: number;
    calendarYear: number;
    kind: ProblemKind;
    severity: ProblemSeverity;
    note: string;
  }[] = [];
  for (const y of years) {
    for (const sig of detectYearKinds(y)) {
      yearSignals.push({
        yearOffset: y.yearOffset,
        calendarYear: y.calendarYear,
        ...sig,
      });
    }
  }
  const grouped = groupConsecutive(yearSignals);

  // Bucket-exhaustion problems are independent (per-bucket, one entry each).
  const exhaustions = detectBucketExhaustions(years).map<Problem>(ex => ({
    id: `bucket_exhausted:${ex.bucket}:${ex.calendarYear}`,
    kind: 'bucket_exhausted',
    severity: 'critical',
    calendarYear: ex.calendarYear,
    endCalendarYear: ex.calendarYear,
    yearOffset: ex.yearOffset,
    title:
      ex.bucket === 'taxable'
        ? `Your taxable bucket runs to zero in ${ex.calendarYear}`
        : ex.bucket === 'hysa'
          ? `Your HYSA runs to zero in ${ex.calendarYear}`
          : `Your 401(k) runs to zero in ${ex.calendarYear}`,
    body:
      `From this year onward, all spending comes from the remaining buckets. ` +
      (ex.bucket === 'retirement'
        ? `If this happens before age 59½, early-withdrawal taxes and penalties typically apply on alternative pulls.`
        : `If this happens before age 59½, dipping into the 401(k) typically triggers tax + 10% penalty unless via 72(t) SEPP.`),
  }));

  const combined = [...grouped, ...exhaustions];
  // Annotate each problem with the glossary term ids referenced in its
  // strings — the UI uses this to render "click to learn" chips alongside
  // the body text. Done after grouping so we scan the final composed copy.
  for (const p of combined) {
    const refs = extractTermRefs(p.title, p.body);
    if (refs.length > 0) p.termRefs = refs;
  }
  // Sort by severity (critical → warning → note), then by year ascending so
  // the user sees the most urgent items at the top while keeping similar
  // severities in chronological order.
  combined.sort((a, b) => {
    const sevDiff = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (sevDiff !== 0) return sevDiff;
    return a.calendarYear - b.calendarYear;
  });
  return opts?.limit != null ? combined.slice(0, opts.limit) : combined;
}

/** Per-year signal map for inline badge rendering in tables / chart rows.
 *  Each entry combines the highest active severity with a plain-language
 *  tooltip composed from the year's `eventNotes` plus engine-derived signals. */
export function getYearProblemMap(years: ProjectionYear[]): Map<number, YearProblems> {
  const map = new Map<number, YearProblems>();
  // Per-row signals
  for (const y of years) {
    const sigs = detectYearKinds(y);
    if (sigs.length === 0) continue;
    let severity: ProblemSeverity | null = null;
    const kinds: ProblemKind[] = [];
    const lines: string[] = [];
    for (const s of sigs) {
      severity = maxSeverity(severity, s.severity);
      if (!kinds.includes(s.kind)) kinds.push(s.kind);
      lines.push(s.note);
    }
    map.set(y.yearOffset, {
      yearOffset: y.yearOffset,
      calendarYear: y.calendarYear,
      severity,
      kinds,
      tooltip: lines.join('\n'),
    });
  }
  // Bucket exhaustion overlays — bumps the row's severity to critical.
  for (const ex of detectBucketExhaustions(years)) {
    const existing = map.get(ex.yearOffset);
    const note = `${BUCKET_LABEL[ex.bucket]} ran to zero this year.`;
    if (existing) {
      existing.severity = maxSeverity(existing.severity, 'critical');
      if (!existing.kinds.includes('bucket_exhausted')) existing.kinds.push('bucket_exhausted');
      existing.tooltip = existing.tooltip ? `${existing.tooltip}\n${note}` : note;
    } else {
      const y = years.find(yy => yy.yearOffset === ex.yearOffset);
      if (!y) continue;
      map.set(ex.yearOffset, {
        yearOffset: ex.yearOffset,
        calendarYear: ex.calendarYear,
        severity: 'critical',
        kinds: ['bucket_exhausted'],
        tooltip: note,
      });
    }
  }
  return map;
}

export function historicalSeries(
  snapshots: Snapshot[],
  accounts: Account[],
): { date: string; netWorth: number; investable: number; liabilities: number }[] {
  const sorted = sortedSnapshotsAsc(snapshots);
  const carry: Record<string, number> = {};
  const out: { date: string; netWorth: number; investable: number; liabilities: number }[] = [];
  for (const s of sorted) {
    Object.assign(carry, s.balances);
    let investable = 0;
    let home = 0;
    let liab = 0;
    for (const a of accounts) {
      if (a.archived) continue;
      const v = carry[a.id] ?? 0;
      if (a.kind === 'home') home += v;
      else if (a.kind === 'mortgage' || a.kind === 'other_liability') liab += v;
      else investable += v;
    }
    out.push({
      date: s.date,
      netWorth: investable + home - liab,
      investable,
      liabilities: liab,
    });
  }
  return out;
}
